import { requireValue } from '../../../shared/require_value.js';
import {
  carrierDisplayName,
  type ChatUiFlightFares,
  type ChatUiFlightOffer,
  type ChatUiMoney,
  FLIGHT_INLINE_OFFERS,
} from '@voll-intelligence/genui-contract';
import { useState } from 'react';
import { ArrowRightIcon, FunnelIcon } from '@phosphor-icons/react';
import { fmtMoneyWhole, fmtTime } from '../../../shared/ui/flight_format.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import type { GenUiDispatch } from '../../../shared/ui/action.js';
import { multiLegSelectionOf } from '../../shared/multi_leg_wire.js';
import { OfferListBody, useOfferFares } from '../../shared/offer_list.js';
import { useResearchOffers } from '../../shared/use_research_offers.js';
import { useFlightTariffPort } from '../../shared/flight_tariff_port.js';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { useGenUiTelemetry } from '../../../shared/ui/telemetry.js';
import { FlightExplorerDialog } from '../../shared/explorer/explorer_dialog.js';
import type {
  ExplorerConfirm,
  FlightExplorerRequest,
  FlightSearchParams,
} from '@voll-intelligence/intelligence-ui-kit';
import { FlightFilterDrawer } from './components/filter_drawer.js';
import type { FareSel, TariffFetchState } from '../../shared/ui/fare_expansion.js';
import '@voll-intelligence/intelligence-ui-kit/flight-offers.css';
import '../../../shared/ui/flight_ui.css';

/** Itens mostrados na lista inline; o restante vai para a drawer "ver todos e
 *  filtrar". Lista empilhada (não carrossel) — mesma sensação do deck de viagens.
 *  O número mora em `shared/schema/limits.ts` porque o AGENTE lê o mesmo: a cena
 *  do mapper rotula `[NO CARTÃO]`/`[ver todos]` e conta o cabeçalho por ele. */
const MAX_INLINE_OFFERS = FLIGHT_INLINE_OFFERS;

/**
 * Lista do drawer = `allOffers` (base, sem badges/política — o produtor manda via
 * `baseOfferForUi`), porém as ofertas que TAMBÉM estão na lista curada (`offers`,
 * enriquecida) reusam o objeto curado por `offerId`. Assim o card no drawer fica
 * EXATAMENTE igual ao da lista inline (mesmo badge + escudo de política), em vez
 * de uma marca genérica. As demais (não curadas) seguem base.
 */
function mergeCuratedIntoAll(
  offers: ChatUiFlightOffer[],
  allOffers: ChatUiFlightOffer[],
): ChatUiFlightOffer[] {
  const curatedById = new Map(offers.map((o) => [o.offerId, o]));
  return allOffers.map((o) => curatedById.get(o.offerId) ?? o);
}

/** Echo do bloco (F) → o request por pernas do explorer
 *  (o card binário só produz one_way/round_trip — nunca multi). */
function toExplorerRequest(params: FlightSearchParams): FlightExplorerRequest {
  return {
    mode: params.returnDate ? 'round_trip' : 'one_way',
    legs: [
      {
        origin: params.origin,
        destination: params.destination,
        departDate: params.departureDate,
      },
    ],
    ...(params.returnDate ? { returnDate: params.returnDate } : {}),
    ...(params.cabin ? { cabin: params.cabin } : {}),
    ...(params.policyTravelerId ? { policyTravelerId: params.policyTravelerId } : {}),
  };
}

/**
 * Os canais do drawer "ver todos" — decididos FORA do corpo do card (o teto de
 * complexidade do componente está no limite). Explorer server-backed: precisa
 * do canal de confirm (challengeId+port+dispatch, o mesmo `interactive` da
 * escada) + do seam `flightSearch` + do echo `search` do bloco. NÃO depende de
 * readOnly: o flip congela o drawer montado em vez de desmontá-lo.
 * `explorerParams` já sai GATED (null = inelegível); `canFilter` liga o CTA
 * (explorer OU o degrade legado client-side).
 *
 * Invariante: o echo é a ÚNICA semente do explorer (contrato do schema:
 * `search` ausente ⇒ degrade legado). Reconstruir params das ofertas re-buscaria
 * SEM cabin/policyTravelerId — preço e veredito de política podiam divergir da
 * busca original — e derivaria a data fatiando `departAt` (timestamp livre:
 * dia UTC ≠ dia local).
 */
function drawerChannels(args: {
  interactive: boolean;
  hasSearchFn: boolean;
  search: FlightSearchParams | undefined;
  allOffers: ChatUiFlightOffer[] | undefined;
  onQuickReply: ((text: string) => void) | undefined;
}): { explorerParams: FlightSearchParams | null; canFilter: boolean } {
  const explorerParams = args.interactive && args.hasSearchFn ? (args.search ?? null) : null;
  const canFilter =
    explorerParams !== null ||
    Boolean(args.allOffers && args.allOffers.length > 0 && args.onQuickReply);
  return { explorerParams, canFilter };
}

/** O drawer do CTA: explorer server-backed quando elegível (`explorerParams`
 *  gated), senão o filtro client-side legado verbatim (degrade). Sem estado
 *  próprio — abrir/fechar é do card. */
function FlightOffersDrawer({
  explorerParams,
  offers,
  allOffers,
  readOnly,
  onConfirm,
  onQuickReply,
  onClose,
}: Readonly<{
  explorerParams: FlightSearchParams | null;
  offers: ChatUiFlightOffer[];
  allOffers?: ChatUiFlightOffer[];
  readOnly?: boolean;
  onConfirm: (confirm: ExplorerConfirm) => void;
  onQuickReply?: (text: string) => void;
  onClose: () => void;
}>) {
  const { t } = useGenUiI18n();
  if (explorerParams) {
    return (
      <FlightExplorerDialog
        request={toExplorerRequest(explorerParams)}
        // RT não semeia (refs de combo são de VOLTA), mas qualquer ref do card
        // ancora o reuso do searchKey da busca dele (#5793); one-way seeda com a
        // lista completa, reusando o objeto curado por offerId (mesmo card).
        {...(explorerParams.returnDate
          ? { reuseAnchorOfferId: offers[0]?.offerId }
          : { seedOffers: [allOffers ? mergeCuratedIntoAll(offers, allOffers) : offers] })}
        readOnly={readOnly}
        onConfirm={onConfirm}
        onClose={onClose}
      />
    );
  }
  return (
    <FlightFilterDrawer
      offers={mergeCuratedIntoAll(offers, requireValue(allOffers))}
      onClose={onClose}
      onSelect={(o) => {
        const s = o.segments[0];
        const where = s ? ` (${fmtTime(s.departAt)} ${s.from}→${s.to})` : '';
        requireValue(onQuickReply)(
          s
            ? t('flightOffers.quoteSpecific', {
                carrier: s.carrier,
                flightNumber: s.flightNumber,
                where,
              })
            : t('flightOffers.quoteGeneric'),
        );
        onClose();
      }}
    />
  );
}

/**
 * Linhas inline: as {@link MAX_INLINE_OFFERS} curadas mais, se preciso, a
 * escolhida resgatada — o card-recibo (`chosen`) tem que MOSTRAR a linha do
 * pick mesmo quando a curadoria a cortou ou quando ele veio do drawer
 * (`allOffers`); sem o resgate, o congelado marcaria escolha nenhuma.
 */
function inlineOffers(
  offers: ChatUiFlightOffer[],
  allOffers: ChatUiFlightOffer[] | undefined,
  chosen: { offerId: string } | undefined,
): ChatUiFlightOffer[] {
  const chosenId = chosen?.offerId;
  const head = offers.slice(0, MAX_INLINE_OFFERS);
  if (!chosenId || head.some((o) => o.offerId === chosenId)) return head;
  const rescued =
    offers.find((o) => o.offerId === chosenId) ?? allOffers?.find((o) => o.offerId === chosenId);
  return rescued ? [...head, rescued] : head;
}

/**
 * Lista de itinerários (resultados de voo). A IA cura uma lista curta; mostramos
 * os {@link MAX_INLINE_OFFERS} primeiros como linhas empilhadas (uma por
 * itinerário) e, quando há lista completa (`allOffers`), um CTA abre a drawer de
 * filtro com o restante.
 *
 * Card interativo novo estilo (produtor v2): quando `challengeId` + o port
 * `flightTariff` + `dispatch` estão presentes, a linha EXPANDE as tarifas inline
 * (fetch client-side com shimmer, cache por oferta) e o pick sai estruturado
 * (`interaction` / `select_offer_fares`) — sem caminho legado de clique-cotar.
 * Sem port/challengeId (histórico legado, preview sem port) as linhas ficam
 * display-only. `onQuickReply` (drawer de filtro, one-way) segue como único
 * caminho de `reply` restante.
 */
type FlightOffersListProps = {
  readonly offers: ChatUiFlightOffer[];
  readonly allOffers?: ChatUiFlightOffer[];
  readonly search?: FlightSearchParams;
  readonly note?: string;
  readonly readOnly?: boolean;
  readonly challengeId?: string;
  readonly autoExpand?: boolean;
  readonly chosen?: { offerId: string; fares: ChatUiFlightFares; price: ChatUiMoney };
  readonly dispatch?: GenUiDispatch;
  readonly onQuickReply?: (text: string) => void;
};

export function FlightOffersList({
  offers,
  allOffers,
  search,
  note,
  readOnly,
  challengeId,
  autoExpand,
  chosen,
  dispatch,
  onQuickReply,
}: FlightOffersListProps) {
  const { t } = useGenUiI18n();
  const telemetry = useGenUiTelemetry();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const tariffPort = useFlightTariffPort();
  const interactive = Boolean(challengeId) && Boolean(tariffPort) && Boolean(dispatch);
  const { flightSearch } = useGenUiConfig();
  const visible = inlineOffers(offers, allOffers, chosen);
  const fares = useOfferFares(visible, {
    enabled: interactive,
    autoExpandId: autoExpand ? (visible[0]?.offerId ?? null) : null,
  });
  // Stale da escada (#2332): a re-busca é um turno da conversa, não o explorer (#5277).
  const research = useResearchOffers({ challengeId, dispatch, enabled: interactive && !readOnly });

  const selectFares = (
    offer: ChatUiFlightOffer,
    position: number,
    state: TariffFetchState,
    picked: { names: string },
    sel: FareSel,
  ) => {
    if (state.phase !== 'ready' || !dispatch || !challengeId) return;
    const bundled = state.view.bundled.length > 0;
    const data = bundled
      ? {
          action: 'select_offer_fares' as const,
          offerId: offer.offerId,
          bundledFareId: requireValue(sel.bundled),
        }
      : {
          action: 'select_offer_fares' as const,
          offerId: offer.offerId,
          outboundFareId: requireValue(sel.outbound),
          ...(sel.return ? { returnFareId: sel.return } : {}),
        };
    const s = offer.segments[0];
    dispatch({
      type: 'interaction',
      interactionId: challengeId,
      text: t('flightOffers.pickedFares', {
        position,
        carrier: carrierDisplayName(s),
        fares: picked.names,
      }),
      data,
    });
  };

  /**
   * O confirm do explorer, nas DUAS formas que ele pode ter.
   *
   * `single` é o MESMO dispatch do pick inline — wire `select_offer_fares`
   * INALTERADO; o spread de `fares` produz exatamente {bundledFareId} |
   * {outboundFareId, returnFareId?}.
   *
   * `multi` existe porque a faixa do explorer é uma fileira de planner inteira: a
   * aba Multitrechos está viva aqui (em TODOS os hosts, desde o destrave de
   * 2026-08-02), e quem a vira monta N trechos e escolhe tarifa em cada um
   * lá dentro. Descartar esse confirm — o que este handler fazia — deixava o
   * "Confirmar seleção" sem efeito NENHUM: sem despacho, sem resposta, drawer
   * aberto. O payload é o mesmo do card do roteiro (`select_multi_leg_fares`, fio
   * inalterado); quem forma `slots.legs`/`legOffers` é a adoção no servidor, que
   * deriva cada trecho da oferta re-lida.
   *
   * `leg` não chega aqui: a casca só o produz quando o host passa `legIndex`, e o
   * card binário nunca passa.
   */
  const confirmExplorer = (c: ExplorerConfirm) => {
    if (!dispatch || !challengeId || c.kind === 'leg') return;
    if (c.kind === 'multi') {
      dispatch({
        type: 'interaction',
        interactionId: challengeId,
        text: t('flightMultiOffers.pickedAll', {
          count: c.legs.length,
          total: fmtMoneyWhole(c.total),
        }),
        data: multiLegSelectionOf(c.legs),
      });
      setDrawerOpen(false);
      return;
    }
    dispatch({
      type: 'interaction',
      interactionId: challengeId,
      text: t('flightOffers.pickedFares', {
        position: c.summary.position,
        carrier: c.summary.carrier,
        fares: c.summary.fareNames,
      }),
      data: { action: 'select_offer_fares' as const, offerId: c.offerId, ...c.fares },
    });
    setDrawerOpen(false);
  };

  // CTA: explorer (server-backed) OU o legado client-side como degrade.
  const { explorerParams, canFilter } = drawerChannels({
    interactive,
    hasSearchFn: Boolean(flightSearch),
    search,
    allOffers,
    onQuickReply,
  });

  // Abre o drawer com telemetria só na transição fechado→aberto: o CTA segue
  // montado (e clicável) com o drawer já aberto, e um reclique nesse estado não
  // é uma nova abertura.
  const openDrawer = () => {
    if (!drawerOpen) {
      telemetry.track('drawer_opened', {
        kind: explorerParams ? 'flight_explorer' : 'flight_filter',
      });
    }
    setDrawerOpen(true);
  };
  return (
    <div className="itin">
      {note && <p className="itin-intro">{note}</p>}
      <div className="itin-top">
        <h4 className="itin-ttl">{t('flightOffers.title')}</h4>
        {/* `visible` e não `offers`: a curadoria pode devolver mais do que as
            {@link MAX_INLINE_OFFERS} linhas que o card empilha, e contar a lista
            curada colocava um terceiro número na tela — cabeçalho, linhas
            visíveis e CTA "ver todos" discordando entre si. O cabeçalho descreve
            o que está à vista; o total da busca é o CTA. */}
        <span className="itin-count">
          {t(visible.length === 1 ? 'flightOffers.itinerary_one' : 'flightOffers.itinerary_other', {
            count: visible.length,
          })}
        </span>
      </div>
      <OfferListBody
        offers={visible}
        fares={fares}
        readOnly={readOnly}
        carousel
        variant="chat"
        chosenOfferId={chosen?.offerId}
        onSelectFares={selectFares}
        {...research}
      />
      {/* Card congelado não oferece a porta (semântica do gate legado): abrir o
          explorer readOnly seria um beco — ele não busca. O drawer JÁ aberto
          segue montado (flip congela sem desmontar). */}
      {canFilter && !readOnly && (
        <button type="button" className="offers-more" onClick={openDrawer}>
          <FunnelIcon size={15} aria-hidden />
          {t('flightOffers.seeAll')}
          {/* A seta aponta pra ONDE o explorer entra — ele desliza pela direita. O
              funil diz o que a porta faz; a seta diz de que lado ela abre. */}
          <ArrowRightIcon size={15} aria-hidden />
        </button>
      )}
      {drawerOpen && canFilter && (
        <FlightOffersDrawer
          explorerParams={explorerParams}
          offers={offers}
          allOffers={allOffers}
          readOnly={readOnly}
          onConfirm={confirmExplorer}
          onQuickReply={onQuickReply}
          onClose={() => {
            setDrawerOpen(false);
          }}
        />
      )}
    </div>
  );
}
